import React, { useState } from "react";
import {
  makeStyles,
  Paper,
  Typography,
  Button,
  Chip,
  Box,
  Collapse,
  Grid,
  Avatar,
  Divider,
  Tooltip
} from "@material-ui/core";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import {
  ExpandMore as ExpandMoreIcon,
  Description as PrescriptionIcon,
  AttachFile as AttachmentIcon,
  Event as EventIcon,
  LocalHospital as DoctorIcon,
  Notes as NotesIcon,
  Warning as AllergyIcon,
  Favorite as HeartIcon,
  Speed as PressureIcon,
  Height as HeightIcon,
  FitnessCenter as WeightIcon,
  Opacity as BloodIcon,
  AcUnit as TempIcon,
  Waves as BreathIcon 
} from "@material-ui/icons";
import { getBackendUrl } from "../../../config";

const useStyles = makeStyles((theme) => ({
  root: {
    maxWidth: 900,
    margin: "0 auto",
  },
  card: {
    marginBottom: theme.spacing(2),
    overflow: "hidden",
    borderRadius: 12,
    border: "1px solid " + theme.palette.divider,
    transition: "box-shadow 0.3s ease, transform 0.2s ease",
    "&:hover": {
      boxShadow: theme.shadows[4],
      borderColor: theme.palette.primary.main,
    },
  },
  header: {
    padding: theme.spacing(2),
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    [theme.breakpoints.up("sm")]: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
    },
    backgroundColor: theme.palette.background.paper,
  },
  dateBox: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(1),
    [theme.breakpoints.up("sm")]: {
      marginBottom: 0,
    },
  },
  dateText: {
    fontWeight: 700,
    fontSize: "1.1rem",
    color: theme.palette.text.primary,
    marginLeft: theme.spacing(1.5),
    textTransform: "capitalize",
  },
  timeText: {
    color: theme.palette.text.secondary,
    fontSize: "0.9rem",
    marginLeft: theme.spacing(1),
    fontWeight: 500,
  },
  infoChips: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
    flexWrap: "wrap",
  },
  expandIcon: {
    transition: "transform 0.3s",
    marginLeft: theme.spacing(1),
    color: theme.palette.action.active,
  },
  expandedIcon: {
    transform: "rotate(180deg)",
  },
  content: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.default,
    borderTop: "1px solid " + theme.palette.divider,
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: "0.9rem",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1.5),
    display: "flex",
    alignItems: "center",
    "& svg": {
      marginRight: theme.spacing(1),
      fontSize: 18,
    }
  },
  vitalCard: {
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(1.5),
    borderRadius: 8,
    border: "1px solid " + theme.palette.divider,
    display: "flex",
    alignItems: "center",
    height: "100%",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
  },
  vitalIconBox: {
    marginRight: theme.spacing(1.5),
    color: theme.palette.primary.main,
    display: "flex",
    alignItems: "center",
  },
  vitalValue: {
    fontWeight: 700,
    fontSize: "1rem",
  },
  vitalLabel: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },
  doctorInfo: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.action.selected,
    borderRadius: 8,
  },
  chipCompleted: {
    backgroundColor: "#4caf50", 
    color: "#fff",
    fontWeight: 600,
  },
  chipPrescription: {
    backgroundColor: theme.palette.info.main,
    color: "#fff",
  },
  actionButton: {
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
    borderRadius: 8,
    textTransform: "none",
  },
  allergyPaper: {
    padding: theme.spacing(1.5),
    border: "1px solid " + theme.palette.error.main,
    backgroundColor: theme.palette.type === "dark"
      ? "rgba(244, 67, 54, 0.2)" 
      : "rgba(244, 67, 54, 0.08)",
  },
}));

const VitalSign = ({ icon: Icon, label, value, unit }) => {
  const classes = useStyles();
  if (!value) return null;
  
  return (
    <Grid item xs={6} sm={4} md={3}>
      <div className={classes.vitalCard}>
        <div className={classes.vitalIconBox}>
          <Icon />
        </div>
        <div>
          <Typography className={classes.vitalValue}>
            {value} <span style={{ fontSize: "0.8em", fontWeight: 400 }}>{unit}</span>
          </Typography>
          <Typography className={classes.vitalLabel}>{label}</Typography>
        </div>
      </div>
    </Grid>
  );
};

const AppointmentCard = ({ appointment, isDoctorOnly }) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);

  const date = parseISO(appointment.date);
  
  const dayName = format(date, "EEEE", { locale: es });
  const dayNum = format(date, "d");
  const monthName = format(date, "MMMM", { locale: es });
  const year = format(date, "yyyy");

  const formattedDate = 
    dayName.charAt(0).toUpperCase() + dayName.slice(1) + " " + 
    dayNum + ", " + 
    monthName.charAt(0).toUpperCase() + monthName.slice(1) + " " + 
    year;
  const formattedTime = appointment.startTime?.substring(0, 5);

  const backendUrl = getBackendUrl();

  const hasClinicalData = Boolean(
    appointment.edad ||
    appointment.temperatura ||
    appointment.presionArterial ||
    appointment.frecuenciaCardiaca ||
    appointment.frecuenciaRespiratoria ||
    appointment.saturacionOxigeno ||
    appointment.glucemiaCapilar ||
    appointment.peso ||
    appointment.talla
  );

  return (
    <Paper className={classes.card} elevation={0}>
      <div className={classes.header} onClick={() => setExpanded(!expanded)}>
        <div className={classes.dateBox}>
          <Avatar style={{ backgroundColor: "#1976d2", width: 40, height: 40 }}>
            <EventIcon fontSize="small" />
          </Avatar>
          <div>
            <Typography className={classes.dateText}>{formattedDate}</Typography>
            <Typography className={classes.timeText}>
              {formattedTime} hs · #{appointment.id}
            </Typography>
          </div>
        </div>

        <div className={classes.infoChips}>
          {appointment.prescriptions?.length > 0 && (
            <Chip
              size="small"
              icon={<PrescriptionIcon style={{ color: "white" }} />}
              label="Receta"
              className={classes.chipPrescription}
            />
          )}
          {appointment.attachments?.length > 0 && (
            <Chip
              size="small"
              icon={<AttachmentIcon />}
              label="Adjuntos"
              variant="outlined"
            />
          )}
          <Chip
            size="small"
            label="Completada"
            className={classes.chipCompleted}
          />
          <ExpandMoreIcon
            className={classes.expandIcon + " " + (expanded ? classes.expandedIcon : "")}
          />
        </div>
      </div>

      <Collapse in={expanded}>
        <div className={classes.content}>
          <div className={classes.doctorInfo}>
            <DoctorIcon color="primary" style={{ marginRight: 12 }} />
            <div>
               <Typography variant="subtitle2" style={{ fontWeight: "bold" }}>
                  {appointment.professionalTitle?.title || "Profesional"}
                  {!isDoctorOnly && appointment.doctor?.name ? " · " + appointment.doctor.name : ""}
               </Typography>
               {appointment.motivoConsulta && (
                 <Typography variant="body2" color="textSecondary">
                   Motivo: {appointment.motivoConsulta}
                 </Typography>
               )}
            </div>
          </div>

          {appointment.notes && (
            <Box mb={3}>
              <Typography className={classes.sectionTitle}>
                <NotesIcon /> Observaciones
              </Typography>
              <Paper variant="outlined" style={{ padding: 12, backgroundColor: "#fff" }}>
                <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>
                  {appointment.notes}
                </Typography>
              </Paper>
            </Box>
          )}

          {hasClinicalData && (
             <Box mb={3}>
                <Typography className={classes.sectionTitle}>
                   <HeartIcon /> Datos Clínicos
                </Typography>
                <Grid container spacing={2}>
                    <VitalSign icon={HeartIcon} label="Edad" value={appointment.edad} unit="años" />
                    <VitalSign icon={WeightIcon} label="Peso" value={appointment.peso} unit="kg" />
                    <VitalSign icon={HeightIcon} label="Talla" value={appointment.talla} unit="cm" />
                    <VitalSign icon={TempIcon} label="Temp" value={appointment.temperatura} unit="°C" />
                    <VitalSign icon={PressureIcon} label="Presión" value={appointment.presionArterial} unit="mmHg" />
                    <VitalSign icon={HeartIcon} label="Frec. Cardíaca" value={appointment.frecuenciaCardiaca} unit="lpm" />
                    <VitalSign icon={BreathIcon} label="Frec. Resp." value={appointment.frecuenciaRespiratoria} unit="rpm" />
                    <VitalSign icon={BloodIcon} label="Saturación" value={appointment.saturacionOxigeno} unit="%" />
                    <VitalSign icon={BloodIcon} label="Glucemia" value={appointment.glucemiaCapilar} unit="mg/dL" />
                </Grid>
             </Box>
          )}

          {appointment.alergias && (
             <Box mb={3}>
                <Typography className={classes.sectionTitle} style={{ color: "#d32f2f" }}>
                   <AllergyIcon style={{ color: "#d32f2f" }} /> Alergias
                </Typography>
                
                <Paper variant="outlined" className={classes.allergyPaper}>
                   <Typography variant="body2" color="error" style={{ fontWeight: 600 }}>
                      {appointment.alergias}
                   </Typography>
                </Paper>
             </Box>
          )}

          <Divider style={{ margin: "16px 0" }} />

          <Grid container spacing={2}>
            {appointment.prescriptions?.length > 0 && (
              <Grid item xs={12} md={6}>
                 <Typography className={classes.sectionTitle}>Recetas Generadas</Typography>
                 {appointment.prescriptions.map((p) => (
                    <Button
                      key={p.id}
                      variant="outlined"
                      color="primary"
                      className={classes.actionButton}
                      startIcon={<PrescriptionIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(backendUrl + "/public" + p.pdfPath, "_blank");
                      }}
                    >
                      Ver Receta #{p.id}
                    </Button>
                 ))}
              </Grid>
            )}

            {appointment.attachments?.length > 0 && (
               <Grid item xs={12} md={6}>
                 <Typography className={classes.sectionTitle}>Archivos Adjuntos</Typography>
                 {appointment.attachments.map((f) => (
                    <Button
                      key={f.id}
                      variant="outlined"
                      color="default"
                      className={classes.actionButton}
                      startIcon={<AttachmentIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(backendUrl + "/public" + f.path, "_blank");
                      }}
                    >
                      {f.name}
                    </Button>
                 ))}
               </Grid>
            )}
          </Grid>

        </div>
      </Collapse>
    </Paper>
  );
};

const HistoryTimeline = ({ appointments, isDoctorOnly }) => {
  const classes = useStyles();

  if (!appointments || appointments.length === 0) {
    return (
      <Box textAlign="center" py={5} color="text.secondary">
         <Typography variant="h6">No hay historial de citas completadas.</Typography>
      </Box>
    );
  }

  return (
    <div className={classes.root}>
      {appointments.map((a) => (
        <AppointmentCard
          key={a.id}
          appointment={a}
          isDoctorOnly={isDoctorOnly}
        />
      ))}
    </div>
  );
};

export default HistoryTimeline;